import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, TrendingUpIcon, MapPinIcon, BarChart3Icon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { LeadFlowAnimation } from '../components/LeadFlowAnimation';
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1317">
      <SEOHead title="Best Marketing Agency in Phoenix for Home Services | Viridia Analytics" description="Ranked among the best marketing agencies in Phoenix for home services. We help HVAC, plumbers, roofers, and landscapers get more leads with Yelp Ads, SEO, and AI lead conversion." canonical="/" keywords="best marketing agencies in phoenix for home services, Phoenix marketing agency, home services marketing Phoenix, HVAC marketing Phoenix, plumber marketing Phoenix, digital marketing agency Phoenix" jsonLd={LOCAL_BUSINESS_SCHEMA} data-id="element-1318" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-1319">
        {/* Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1320">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-viridia/20 blur-[120px] animate-pulse-slow" data-id="element-1321"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia-dark/20 blur-[150px] animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-1322"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1323">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1324">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-1325">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-1326">
                <span className="w-2 h-2 rounded-full bg-viridia animate-pulse" data-id="element-1327"></span>
                Phoenix's Premier Home Services Marketing Agency
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1328">
                Get More Leads For Your{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-viridia to-emerald-300" data-id="element-1329">
                  Home Service Business
                </span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-lg sm:text-xl text-gray-400 mb-10 leading-relaxed" data-id="element-1330">
                We are a specialized digital marketing agency in Phoenix helping
                HVAC, plumbers, roofers, and landscapers dominate their local
                market with expert Yelp Ads, SEO, and AI-powered lead
                conversion.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row gap-4" data-id="element-1331">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:-translate-y-1" data-id="element-1332">
                  Book a Strategy Call
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1333" />
                </Link>
                <a href="#services" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-transparent border border-gray-700 rounded-lg hover:bg-viridia-surface hover:border-gray-600 transition-all duration-300" data-id="element-1334">
                  See Our Services
                </a>
              </motion.div>
            </div>

            {/* Hero Visual — Lead Flow Animation */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.4
          }} className="relative hidden lg:flex items-center justify-center h-[500px]" data-id="element-1335">
              <LeadFlowAnimation data-id="element-1336" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY BAR */}
      <section className="bg-viridia-light py-10 border-y border-gray-200" data-id="element-1337">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1338">
          <ScrollReveal data-id="element-1339">
            <h2 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6" data-id="element-1340">
              Trusted by Top Phoenix Home Service Businesses
            </h2>
            <div className="flex overflow-x-auto pb-4 md:pb-0 md:grid md:grid-cols-7 gap-8 items-center justify-between opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-1341">
              {['HVAC', 'Plumbing', 'Landscaping', 'Roofing', 'Pest Control', 'Electricians', 'Remodeling'].map((industry, i) => <div key={i} className="flex-shrink-0 flex items-center justify-center min-w-[120px] md:min-w-0" data-id="element-1342">
                  <span className="text-xl font-bold text-gray-800" data-id="element-1343">
                    {industry}
                  </span>
                </div>)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* NEW SEO-RICH INDUSTRIES SECTION */}
      <section className="py-24 bg-viridia-black border-b border-gray-800" data-id="element-1344">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1345">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1346">
            <ScrollReveal data-id="element-1347">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1348">
                Why We Are the Best Marketing Agency in Phoenix for Home
                Services
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1349">
                Generic marketing agencies don't understand the home services
                industry. They don't know that a missed call from a homeowner
                with a broken AC in July means a lost $5,000 job.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1350">
                As a specialized Phoenix marketing agency, we focus exclusively
                on local service businesses. We know how to rank your business
                in the Google Map Pack, how to run profitable Yelp Ads (we are
                former Yelp insiders), and how to automate your lead follow-up
                so you book more jobs.
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-1351">
                {['HVAC & AC Repair', 'Plumbing Services', 'Roofing Contractors', 'Landscaping & Turf', 'Pest Control', 'Electrical Contractors', 'Painting Contractors', 'Home Remodeling'].map((industry, i) => <div key={i} className="flex items-center text-gray-300" data-id="element-1352">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-2 flex-shrink-0" data-id="element-1353" />
                    <span className="font-medium" data-id="element-1354">{industry}</span>
                  </div>)}
              </div>
            </ScrollReveal>
            <ScrollReveal direction="left" data-id="element-1355">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 shadow-2xl relative" data-id="element-1356">
                <div className="absolute -top-4 -right-4 bg-viridia text-viridia-black font-bold px-4 py-2 rounded-lg transform rotate-3 shadow-lg" data-id="element-1357">
                  Local Phoenix Experts
                </div>
                <h3 className="text-2xl font-bold text-white mb-6" data-id="element-1358">
                  The Viridia Advantage
                </h3>
                <ul className="space-y-6" data-id="element-1359">
                  <li className="flex items-start" data-id="element-1360">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center text-viridia mr-4 flex-shrink-0" data-id="element-1361">
                      <span className="font-bold" data-id="element-1362">1</span>
                    </div>
                    <div data-id="element-1363">
                      <h4 className="text-lg font-bold text-white mb-1" data-id="element-1364">
                        Former Yelp Insiders
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1365">
                        We know exactly how to manipulate the Yelp algorithm to
                        get you cheaper, higher-quality leads.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start" data-id="element-1366">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center text-viridia mr-4 flex-shrink-0" data-id="element-1367">
                      <span className="font-bold" data-id="element-1368">2</span>
                    </div>
                    <div data-id="element-1369">
                      <h4 className="text-lg font-bold text-white mb-1" data-id="element-1370">
                        Speed-to-Lead AI
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1371">
                        Our AI chatbots and missed-call text-back systems ensure
                        you respond to every lead in under 60 seconds.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start" data-id="element-1372">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center text-viridia mr-4 flex-shrink-0" data-id="element-1373">
                      <span className="font-bold" data-id="element-1374">3</span>
                    </div>
                    <div data-id="element-1375">
                      <h4 className="text-lg font-bold text-white mb-1" data-id="element-1376">
                        Local Market Knowledge
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1377">
                        Years of hands-on experience in the Phoenix market. We
                        know the neighborhoods, the competition, and what
                        actually works for local service businesses.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-viridia-black relative" data-id="element-1378">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1379">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" data-id="element-1380">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-1381">
              How It Works
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1382">
              From first click to booked job — here's the system.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative" data-id="element-1383">
            {/* Connecting Line for Desktop */}
            <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gray-800 z-0" data-id="element-1384"></div>

            {[{
            step: '01',
            title: 'We Audit Your Marketing',
            desc: 'Free strategy call to review your current ads, website, and follow-up process.',
            icon: <SearchIcon className="w-6 h-6 text-viridia" data-id="element-1385" />
          }, {
            step: '02',
            title: 'Launch Targeted Campaigns',
            desc: 'We build and manage ads on Google, Meta, and Yelp that reach your ideal customers.',
            icon: <TargetIcon className="w-6 h-6 text-viridia" data-id="element-1386" />
          }, {
            step: '03',
            title: 'Automate Your Follow-Up',
            desc: 'Missed calls get texted back. Leads get nurtured. Bookings happen automatically.',
            icon: <MessageSquareIcon className="w-6 h-6 text-viridia" data-id="element-1387" />
          }, {
            step: '04',
            title: 'Watch Your Revenue Grow',
            desc: 'Track every lead, every job, every dollar with clear reporting and monthly strategy calls.',
            icon: <TrendingUpIcon className="w-6 h-6 text-viridia" data-id="element-1388" />
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-1389">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-viridia/50 transition-colors duration-300 group" data-id="element-1390">
                  <div className="w-16 h-16 rounded-full bg-viridia-black border-2 border-viridia flex items-center justify-center mb-6 shadow-[0_0_15px_rgba(16,185,129,0.2)] group-hover:scale-110 transition-transform duration-300" data-id="element-1391">
                    {item.icon}
                  </div>
                  <div className="text-viridia font-bold text-sm mb-2" data-id="element-1392">
                    STEP {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1393">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1394">{item.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section id="services" className="py-24 bg-viridia-light" data-id="element-1395">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1396">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" data-id="element-1397">
            <h2 className="text-3xl md:text-5xl font-bold text-viridia-black mb-4" data-id="element-1398">
              Everything You Need to Grow
            </h2>
            <p className="text-xl text-gray-600" data-id="element-1399">
              Stop juggling multiple agencies. We handle the entire customer
              journey.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1400">
            {[{
            title: 'AI Lead Conversion',
            desc: 'Smart systems that respond to leads, book jobs, and follow up — so you never miss an opportunity.',
            icon: <ZapIcon className="w-8 h-8 text-viridia" data-id="element-1401" />,
            link: '/ai-lead-conversion'
          }, {
            title: 'Ad Campaigns',
            desc: 'Expert ad management across Google, Meta, and Yelp. We turn your ad spend into qualified leads.',
            icon: <TargetIcon className="w-8 h-8 text-viridia" data-id="element-1402" />,
            link: '/ad-campaigns'
          }, {
            title: 'Web Design & SEO',
            desc: 'Fast, conversion-focused websites with local SEO that turns searches into customers.',
            icon: <GlobeIcon className="w-8 h-8 text-viridia" data-id="element-1403" />,
            link: '/web-design-seo'
          }].map((service, index) => <ScrollReveal key={index} delay={index * 0.15} data-id="element-1404">
                <Link to={service.link} className="block h-full" data-id="element-1405">
                  <div className="bg-viridia-surface rounded-2xl p-8 h-full border-t-4 border-transparent hover:border-viridia transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group" data-id="element-1406">
                    <div className="w-16 h-16 rounded-xl bg-viridia/10 flex items-center justify-center mb-6 group-hover:bg-viridia/20 transition-colors" data-id="element-1407">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1408">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-8 leading-relaxed" data-id="element-1409">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-viridia font-medium group-hover:translate-x-2 transition-transform" data-id="element-1410">
                      Learn More <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1411" />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* CASE STUDIES PREVIEW */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1412">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1413">
          <ScrollReveal data-id="element-1414">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-1415">
              <div className="max-w-2xl" data-id="element-1416">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1417">
                  Real Results for Real Businesses
                </h2>
                <p className="text-lg text-gray-400" data-id="element-1418">
                  We don't just talk about growth. We build systems that
                  generate revenue.
                </p>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors" data-id="element-1419">
                View All Case Studies{' '}
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1420" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1421">
            <ScrollReveal delay={0.1} data-id="element-1422">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-center relative overflow-hidden group" data-id="element-1423">
                <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/5 rounded-full blur-[50px] group-hover:bg-viridia/10 transition-colors" data-id="element-1424"></div>
                <div className="relative z-10" data-id="element-1425">
                  <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center mb-6" data-id="element-1426">
                    <BarChart3Icon className="w-6 h-6 text-viridia" data-id="element-1427" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1428">
                    Scaling a Local Service Business
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1429">
                    See how we implemented our Full Automation Suite and
                    targeted ad campaigns to stop leads from slipping through
                    the cracks.
                  </p>
                  <a href="https://case-study.viridiaanalytics.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-viridia font-semibold hover:text-white transition-colors" data-id="element-1430">
                    Read Case Study <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1431" />
                  </a>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1432">
              <div className="bg-viridia-surface/50 border border-gray-800 border-dashed rounded-2xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-1433">
                <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-6" data-id="element-1434">
                  <span className="bg-gray-800 text-gray-300 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider" data-id="element-1435">
                    Data Collection in Progress
                  </span>
                  <p className="text-sm text-gray-400 max-w-xs" data-id="element-1436">
                    [Need exact lead or revenue increase metric, client
                    industry, city, and approved dashboard screenshot from
                    Kevin]
                  </p>
                </div>
                <div className="opacity-20 blur-sm" data-id="element-1437">
                  <div className="w-12 h-12 bg-gray-700 rounded-xl mb-6 mx-auto" data-id="element-1438"></div>
                  <div className="h-6 w-48 bg-gray-700 rounded mx-auto mb-4" data-id="element-1439"></div>
                  <div className="h-4 w-full bg-gray-700 rounded mb-2" data-id="element-1440"></div>
                  <div className="h-4 w-3/4 bg-gray-700 rounded mx-auto" data-id="element-1441"></div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1442">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1443">
          <ScrollReveal data-id="element-1444">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1445">
              <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center mx-auto mb-6 text-viridia" data-id="element-1446">
                <MapPinIcon className="w-6 h-6" data-id="element-1447" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1448">
                Our Service Areas
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1449">
                While our headquarters is located in Phoenix, Arizona, we
                partner with home service businesses across the Greater Phoenix
                area and Southern California.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto" data-id="element-1450">
            <ScrollReveal delay={0.1} data-id="element-1451">
              <div className="bg-viridia-black border border-gray-800 rounded-xl p-6" data-id="element-1452">
                <h3 className="text-xl font-bold text-white mb-4 border-b border-gray-800 pb-4" data-id="element-1453">
                  Greater Phoenix Area
                </h3>
                <ul className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-1454">
                  {['Phoenix', 'Scottsdale', 'Glendale', 'Peoria', 'Chandler', 'Mesa', 'Queen Creek', 'Buckeye', 'Maricopa County'].map((city, i) => <li key={i} className="flex items-center text-gray-400" data-id="element-1455">
                      <span className="w-1.5 h-1.5 rounded-full bg-viridia mr-2" data-id="element-1456"></span>
                      {city}
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1457">
              <div className="bg-viridia-black border border-gray-800 rounded-xl p-6" data-id="element-1458">
                <h3 className="text-xl font-bold text-white mb-4 border-b border-gray-800 pb-4" data-id="element-1459">
                  California
                </h3>
                <ul className="grid grid-cols-1 gap-y-3 gap-x-4" data-id="element-1460">
                  {['Long Beach', 'Los Angeles Area'].map((city, i) => <li key={i} className="flex items-center text-gray-400" data-id="element-1461">
                      <span className="w-1.5 h-1.5 rounded-full bg-viridia mr-2" data-id="element-1462"></span>
                      {city}
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 overflow-hidden" data-id="element-1463">
        <div className="absolute inset-0 bg-gradient-to-br from-viridia-dark to-viridia-black z-0" data-id="element-1464"></div>
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2)_0,transparent_100%)] z-0" data-id="element-1465"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1466">
          <ScrollReveal data-id="element-1467">
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-1468">
              Ready to Stop Losing Leads?
            </h2>
            <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto" data-id="element-1469">
              Book a free strategy call and we'll show you exactly where you're
              leaving money on the table.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:-translate-y-1 mb-6" data-id="element-1470">
              Book Your Free Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-1471" />
            </Link>
            <p className="text-emerald-200/80 font-medium" data-id="element-1472">
              No contracts. No obligation. Just results.
            </p>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}